import React, { useMemo, useState } from "react";
import { Badge } from "../../badge";
import { Button } from "../../button";
import { Input } from "../../input";
import { Textarea } from "../../textarea";
import { FormField, FormShell } from "../form-shell";

export interface CrmTemplateEditorProps {
  initial?: {
    slug?: string;
    name?: string;
    subject?: string;
    body_text?: string;
  };
  onSubmit?: (input: { slug: string; name: string; subject: string; body_text: string }) => void | Promise<void>;
  /** Render inside a dialog: drop the outer Card + its header (the dialog owns
   *  the title/description), keeping just the fields + submit row. */
  embedded?: boolean;
}

/**
 * CRM message-template editor. Named `CrmTemplateEditor` to disambiguate from
 * the generic `TemplateEditor` (`blocks/settings/template-editor.tsx`, the
 * barrel-exported one CLAUDE.md rule #8 points at) — the two have different
 * prop shapes, so the shared `TemplateEditor` name was a landmine.
 */
export function CrmTemplateEditor({ initial, onSubmit, embedded = false }: CrmTemplateEditorProps) {
  const [slug, setSlug] = useState(initial?.slug ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [subject, setSubject] = useState(initial?.subject ?? "");
  const [body, setBody] = useState(initial?.body_text ?? "");
  const tokens = useMemo(() => {
    const set = new Set<string>();
    for (const m of (subject + " " + body).matchAll(/\{\{\s*([a-z0-9_.]+)\s*\}\}/gi)) {
      set.add(m[1]!);
    }
    return [...set];
  }, [subject, body]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!onSubmit) return;
    await onSubmit({ slug, name, subject, body_text: body });
  }

  return (
    <FormShell
      title={embedded ? undefined : "Template editor"}
      description={
        embedded ? undefined : "Save a reusable email body with merge tokens."
      }
      compact={embedded}
      onSubmit={handleSubmit}
      footer={
        onSubmit && (
          <Button
            type="submit"
            size="sm"
            disabled={!slug || !name || !subject}
          >
            Save template
          </Button>
        )
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Slug" required>
          <Input
            placeholder="intro"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
          />
        </FormField>
        <FormField label="Name" required>
          <Input
            placeholder="Outbound intro"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </FormField>
      </div>
      <FormField label="Subject" required>
        <Input
          placeholder="Quick intro from {{owner.display_name}}"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
      </FormField>
      <FormField
        label="Body"
        help="Use {{lead.display_name}}, {{contact.given_name}}, {{owner.display_name}} merge tokens."
      >
        <Textarea
          className="min-h-48 resize-y font-mono"
          placeholder="Hi {{contact.given_name}}, …"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </FormField>
      <div className="flex flex-wrap items-center gap-2 type-body text-muted-foreground">
        <span>Tokens:</span>
        {tokens.length === 0 ? (
          <span>(none)</span>
        ) : (
          tokens.map((t) => (
            <Badge key={t} variant="outline">
              {`{{${t}}}`}
            </Badge>
          ))
        )}
      </div>
    </FormShell>
  );
}
